import type { Device } from '@/types/api';

/**
 * Returns true iff the device has transmitted since being assigned to its
 * current unit. Used to suppress signal/usage rendering on cards and stat
 * tiles when the visible values would be carried over from a prior
 * assignment (e.g. a meter that was running on Unit A then reassigned to
 * Unit B without a new uplink yet).
 *
 * The rule is symmetric across surfaces:
 *   - Setup-mode device grid (where this pattern originated).
 *   - Property device cards.
 *   - Device detail stat tiles.
 *
 * When `unitAssignedAt` is null (device not yet assigned to a unit) no
 * transmission can be "fresh at this unit", so callers default to the
 * awaiting-uplink visual treatment.
 */
export function hasFreshTransmission(
  device: Pick<Device, 'lastSeenAt' | 'unitAssignedAt'>,
): boolean {
  if (!device.unitAssignedAt || !device.lastSeenAt) return false;
  return new Date(device.lastSeenAt).getTime() >= new Date(device.unitAssignedAt).getTime();
}
